'use client';

import { useSelector } from '@legendapp/state/react';
import { type ReactNode, useLayoutEffect, useState } from 'react';
import { Carousel } from '@/components/carousel/Carousel';
import { useBreakpointDown$ } from '@/hooks/useBreakpointDown$';
import { cn } from '@/lib/cn';
import type { CmsContainerWidth } from '@/services/cms-sdk';

export interface FleetTypeCardsCarouselProps {
    children?: ReactNode;
    itemCount: number;
    loop: boolean;
    middleIndex: number;
    showNavigation: boolean;
    showPagination: boolean;
    width?: CmsContainerWidth;
}

const widthSmDown = 240;
const spacingSmDown = 24;
const widthMdUp = 488;
const spacingMdUp = 64;

const getScrollableWidth = (itemCount: number, itemWidth: number, spacing: number) => itemCount * (itemWidth + spacing) - spacing;

export function FleetTypeCardsCarousel({ children, itemCount, loop, middleIndex, showNavigation, showPagination, width }: FleetTypeCardsCarouselProps) {
    const [container, setContainer] = useState<HTMLDivElement | null>(null);
    const [containerWidth, setContainerWidth] = useState<number | undefined>(undefined);

    useLayoutEffect(() => {
        if (!container) {
            return;
        }

        const resizeHandler = () =>
            setContainerWidth((prev) => {
                if (container.offsetWidth) {
                    return container.offsetWidth;
                }

                if (prev) {
                    return prev;
                }

                return window.innerWidth;
            });

        const observe = (entries: IntersectionObserverEntry[]) => {
            entries.forEach((entry) => {
                if (entry.target === container && entry.isIntersecting) {
                    resizeHandler();
                }
            });
        };
        const intersectionObserver = new IntersectionObserver(observe, {
            root: null,
            rootMargin: '0px',
            threshold: 0.0,
        });
        intersectionObserver.observe(container);

        const resizeObserver = new ResizeObserver(resizeHandler);
        resizeObserver.observe(container);

        window.addEventListener('resize', resizeHandler);
        resizeHandler();

        return () => {
            window.removeEventListener('resize', resizeHandler);
            intersectionObserver.disconnect();
            resizeObserver.disconnect();
        };
    }, [container]);

    const isSm$ = useBreakpointDown$('md');
    const isSm = useSelector(() => isSm$.get());
    const carouselWidth = isSm ? getScrollableWidth(itemCount, widthSmDown, spacingSmDown) : getScrollableWidth(itemCount, widthMdUp, spacingMdUp);

    const hasWidth = width !== undefined && width !== 'Full';
    const active = containerWidth !== undefined ? carouselWidth > containerWidth : false;
    const enabledWide = active && !hasWidth;

    return (
        <div className="flex justify-center fleet-type-card-block" ref={setContainer}>
            <Carousel
                className={cn('[--slide-width:240px] md:[--slide-width:488px]', '[--carousel-spacing:24px] md:[--carousel-spacing:64px]', {
                    '[&_.carousel-viewport]:max-w-[calc(100vw-1rem)] [&_.carousel-viewport]:mx-auto': !hasWidth,
                    '[&_.carousel-container>*]:ml-[calc(var(--carousel-spacing)/2)]': true,
                    '[&_.carousel-container>*]:mr-[calc(var(--carousel-spacing)/2)]': true,
                    'md:[&_.carousel-container]:justify-center': !enabledWide && !hasWidth && !active,
                })}
                initClassName={cn({ '[&_.carousel-container]:translate-x-(--container-offset)': enabledWide })}
                key={`carousel-${carouselWidth}-${active ? 'active' : 'inactive'}`}
                style={{
                    '--start-slide-index': middleIndex,
                    '--slide-width-total': 'calc(var(--slide-width) + var(--carousel-spacing))',
                    '--start-slide-offset': 'calc((var(--start-slide-index) * var(--slide-width-total)) + (var(--carousel-spacing) / 2 ))',
                    '--slide-center': 'calc(var(--slide-width) / 2)',
                    '--container-center': 'calc(var(--slide-center) + var(--start-slide-offset))',
                    '--viewport-center': hasWidth ? 'calc(100% / 2)' : 'min(calc(100% / 2), 50vw)',
                    '--container-offset': 'calc(var(--viewport-center) - var(--container-center))',
                }}
                options={{
                    active,
                    align: 'center',
                    containScroll: false,
                    loop: loop,
                    skipSnaps: true,
                    startIndex: itemCount > 2 ? middleIndex : 0,
                }}
                showNavigation={showNavigation}
                showPagination={showPagination}
                navContainerClassName={cn('min-w-[var(--slide-width)] m-auto [&_.btn-prev]:left-2 [&_.btn-next]:right-2 inset-y-auto top-18.5 md:top-37.5', {
                    'max-w-640': enabledWide,
                    'max-w-wide': !enabledWide,
                })}
            >
                {children}
            </Carousel>
        </div>
    );
}
